<template>
    <div>
        <h6 class="mb-3">
            <MessageSquare :size="18" class="me-2" style="vertical-align: text-bottom;" />
            Messages
        </h6>

        <!-- New Message Form -->
        <div class="card mb-3">
            <div class="card-body p-2">
                <div class="row g-2">
                    <div class="col-md-6">
                        <tech-ac
                            v-model="newMessage.RecipientUserId"
                            placeholder="Recipient (optional)"
                            inputclass="form-control form-control-sm"
                        />
                    </div>
                    <div class="col-md-6">
                        <div class="form-check form-check-inline mt-2">
                            <input class="form-check-input" type="checkbox" id="isPrivate" v-model="newMessage.IsPrivate">
                            <label class="form-check-label" for="isPrivate">
                                <small>Private Message</small>
                            </label>
                        </div>
                    </div>
                </div>
                <div class="mt-2">
                    <textarea 
                        class="form-control form-control-sm" 
                        rows="2" 
                        v-model="newMessage.Body"
                        placeholder="Type a message..."
                        @keydown.ctrl.enter="sendMessage"
                    ></textarea>
                </div>
                <div class="d-flex justify-content-end mt-2">
                    <button 
                        class="btn btn-sm btn-primary" 
                        @click="sendMessage" 
                        :disabled="!newMessage.Body || sending"
                    >
                        <Send :size="14" class="me-1" />
                        {{ sending ? 'Sending...' : 'Send' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Messages List -->
        <div v-if="loading" class="text-center py-3">
            <div class="spinner-border spinner-border-sm" role="status"></div>
            <small class="ms-2 text-muted">Loading messages...</small>
        </div>

        <div v-else-if="messages.length === 0" class="text-muted text-center py-3">
            <MessageSquare :size="24" class="mb-2 d-block mx-auto opacity-50" />
            <small>No messages yet.</small>
        </div>

        <div v-else class="messages-list">
            <div 
                v-for="msg in messages" 
                :key="msg.Id" 
                class="card mb-2"
                :class="{ 'border-warning': msg.IsPrivate }"
            >
                <div class="card-body p-2">
                    <div class="d-flex justify-content-between align-items-start">
                        <div>
                            <strong class="small">{{ msg.SenderName || 'System' }}</strong>
                            <span v-if="msg.RecipientName" class="text-muted small ms-1">
                                → {{ msg.RecipientName }}
                            </span>
                            <span v-if="msg.IsPrivate" class="badge bg-warning text-dark ms-1" style="font-size: 0.65rem;">
                                <Lock :size="10" class="me-1" />Private
                            </span>
                        </div>
                        <small class="text-muted">{{ formatDate(msg.CreatedDate) }}</small>
                    </div>
                    <div class="small mt-1" style="white-space: pre-wrap;">{{ msg.Body }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, onUnmounted } from "vue";
import ApiService from "@/services/ApiService";
import Http from "@/services/Http";
import Toast from "@/services/Toast";
import TechAc from "@/components/Techs/TechAc.vue";
import { MessageSquare, Send, Lock } from "lucide-vue-next";

const props = defineProps({
    requestId: {
        type: Number,
        default: 0
    }
});

const messages = ref<any[]>([]);
const loading = ref(false);
const sending = ref(false);
const newMessage = ref({
    Body: '',
    RecipientUserId: null as number | null,
    IsPrivate: false
});

const unwatch: Function[] = [];

const loadMessages = async () => {
    if (!props.requestId) return;
    loading.value = true;
    try {
        const response: any = await Http.get('/notification/list', {
            RelatedEntityType: 'Request',
            RelatedEntityId: props.requestId,
            Mailbox: 'all',
            PageSize: 100,
            SortDirection: 'asc'
        });
        // Http.get returns data.Data by default; the notification list endpoint
        // returns { Data: [...], Meta: {...} } inside the wrapper
        const result = response?.Data || response || [];
        messages.value = Array.isArray(result) ? result : [];
    } catch (error) {
        console.error('Error loading messages:', error);
        messages.value = [];
    } finally {
        loading.value = false;
    }
};

const sendMessage = async () => {
    if (!newMessage.value.Body || !props.requestId) return;
    sending.value = true;
    try {
        await Http.post('/notification/save', {
            Subject: 'Job Message',
            Body: newMessage.value.Body,
            NotificationType: 'Message',
            RelatedEntityType: 'Request',
            RelatedEntityId: props.requestId,
            RecipientUserId: newMessage.value.RecipientUserId || null,
            IsPrivate: newMessage.value.IsPrivate
        });
        newMessage.value.Body = '';
        newMessage.value.RecipientUserId = null;
        newMessage.value.IsPrivate = false;
        await loadMessages();
    } catch (error) {
        console.error('Error sending message:', error);
    } finally {
        sending.value = false;
    }
};

const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString();
};

unwatch.push(watch(() => props.requestId, (nv) => {
    if (nv) loadMessages();
}, { immediate: true }));

onUnmounted(() => {
    unwatch.forEach(fn => fn());
});
</script>
